'use client';
import React, { useState, useEffect } from 'react';
import Link from 'next/link';
import Image from 'next/image';
import { usePathname } from 'next/navigation';
import Button from '../ui/Button';
import { CONTENT, ARIA_LABELS } from '@/constants/content';
import { getAssetPath } from '@/lib/utils';

const Header: React.FC = () => {
  const [menuOpen, setMenuOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);
  const pathname = usePathname();

  // Handle body scroll lock when mobile menu is open
  useEffect(() => {
    if (menuOpen) {
      // Prevent body scroll when menu is open
      document.body.style.overflow = 'hidden';
    } else {
      // Restore body scroll when menu is closed
      document.body.style.overflow = 'unset';
    }

    // Cleanup function to ensure scroll is restored
    return () => {
      document.body.style.overflow = 'unset';
    };
  }, [menuOpen]);

  useEffect(() => {
    const handleScroll = () => {
      setScrolled(window.scrollY > 10);
    };

    // Check initial scroll position immediately
    handleScroll();

    // Also check after a small delay to handle browser scroll restoration
    const timeoutId = setTimeout(() => {
      handleScroll();
    }, 100);

    window.addEventListener('scroll', handleScroll);

    return () => {
      window.removeEventListener('scroll', handleScroll);
      clearTimeout(timeoutId);
    };
  }, []);

  // Helper function to determine if a link is active
  const isActive = (href: string) => {
    return pathname === href;
  };

  return (
    <header data-component-id="src/components/common/Header.tsx:58:4" data-component-path="src/components/common/Header.tsx" data-component-line="58" data-component-end-line="194" data-component-file="Header.tsx" data-component-name="header" data-component-content="%7B%22elementName%22%3A%22header%22%7D"
      className={`
      w-full
      sticky top-0 z-50
      px-4 pt-8 pb-4 h-20 sm:px-8 md:px-16 lg:px-24 xl:px-[108px] mb-[71px] sm:mb-20 lg:h-[72px]
      transition-all duration-300
      ${menuOpen ? 'bg-action-2' : scrolled ? 'bg-main shadow-sm' : 'bg-transparent'}
    `}
    >
      <div data-component-id="src/components/common/Header.tsx:67:6" data-component-path="src/components/common/Header.tsx" data-component-line="67" data-component-end-line="193" data-component-file="Header.tsx" data-component-name="div" data-component-content="%7B%22elementName%22%3A%22div%22%2C%22className%22%3A%22w-full%20max-w-%5B1224px%5D%20mx-auto%20h-full%22%7D" className="w-full max-w-[1224px] mx-auto h-full">
        <div data-component-id="src/components/common/Header.tsx:68:8" data-component-path="src/components/common/Header.tsx" data-component-line="68" data-component-end-line="150" data-component-file="Header.tsx" data-component-name="div" data-component-content="%7B%22elementName%22%3A%22div%22%2C%22className%22%3A%22flex%20justify-between%20items-center%20h-full%22%7D" className="flex justify-between items-center h-full">
          {/* Logo */}
          <div data-component-id="src/components/common/Header.tsx:70:10" data-component-path="src/components/common/Header.tsx" data-component-line="70" data-component-end-line="84" data-component-file="Header.tsx" data-component-name="div" data-component-content="%7B%22elementName%22%3A%22div%22%2C%22className%22%3A%22flex-shrink-0%22%7D" className="flex-shrink-0">
            <Link data-component-id="src/components/common/Header.tsx:71:12" data-component-path="src/components/common/Header.tsx" data-component-line="71" data-component-end-line="83" data-component-file="Header.tsx" data-component-name="Link" data-component-content="%7B%22elementName%22%3A%22Link%22%2C%22href%22%3A%22%2Fhome%22%2C%22className%22%3A%22%20rounded%22%7D" href="/home" className=" rounded">
              <Image data-component-id="src/components/common/Header.tsx:72:14" data-component-path="src/components/common/Header.tsx" data-component-line="72" data-component-end-line="82" data-component-file="Header.tsx" data-component-name="Image" data-component-content="%7B%22elementName%22%3A%22Image%22%2C%22alt%22%3A%22Sirio%20Strategies%20-%20Home%22%7D"
                src={
                  menuOpen
                    ? getAssetPath('/images/sirio_white.svg')
                    : getAssetPath('/images/sirio.svg')
                }
                alt="Sirio Strategies - Home"
                width={52.2}
                height={20}
                priority
              />
            </Link>
          </div>

          {/* Hamburger Menu Icon (Mobile only) */}
          <button data-component-id="src/components/common/Header.tsx:87:10" data-component-path="src/components/common/Header.tsx" data-component-line="87" data-component-end-line="116" data-component-file="Header.tsx" data-component-name="button" data-component-content="%7B%22elementName%22%3A%22button%22%2C%22className%22%3A%22block%20sm%3Ahidden%20rounded%20relative%20z-50%22%7D"
            className="block sm:hidden rounded relative z-50"
            aria-label={menuOpen ? 'Close menu' : 'Open menu'}
            aria-expanded={menuOpen}
            onClick={() => setMenuOpen(!menuOpen)}
          >
            {menuOpen ? (
              <Image data-component-id="src/components/common/Header.tsx:94:14" data-component-path="src/components/common/Header.tsx" data-component-line="94" data-component-end-line="100" data-component-file="Header.tsx" data-component-name="Image" data-component-content="%7B%22elementName%22%3A%22Image%22%2C%22alt%22%3A%22Close%20menu%22%2C%22className%22%3A%22w-8%20h-8%22%7D"
                src={getAssetPath('/images/x_mob.svg')}
                alt="Close menu"
                width={32}
                height={32}
                className="w-8 h-8"
              />
            ) : (
              <svg data-component-id="src/components/common/Header.tsx:102:14" data-component-path="src/components/common/Header.tsx" data-component-line="102" data-component-end-line="114" data-component-file="Header.tsx" data-component-name="svg" data-component-content="%7B%22elementName%22%3A%22svg%22%2C%22className%22%3A%22w-8%20h-8%20text-text-primary%22%7D"
                className="w-8 h-8 text-text-primary"
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
              >
                <path data-component-id="src/components/common/Header.tsx:108:16" data-component-path="src/components/common/Header.tsx" data-component-line="108" data-component-end-line="113" data-component-file="Header.tsx" data-component-name="path" data-component-content="%7B%22elementName%22%3A%22path%22%7D"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M4 6h16M4 12h16M4 18h16"
                />
              </svg>
            )}
          </button>

          {/* Desktop Navigation */}
          <nav data-component-id="src/components/common/Header.tsx:119:10" data-component-path="src/components/common/Header.tsx" data-component-line="119" data-component-end-line="136" data-component-file="Header.tsx" data-component-name="nav" data-component-content="%7B%22elementName%22%3A%22nav%22%2C%22className%22%3A%22hidden%20sm%3Aflex%20items-center%20gap-x-%5B40px%5D%20%22%7D"
            className="hidden sm:flex items-center gap-x-[40px] "
            aria-label={ARIA_LABELS.navigation.main}
          >
            {CONTENT.navigation.links.map((link) => (
              <Link data-component-id="src/components/common/Header.tsx:124:14" data-component-path="src/components/common/Header.tsx" data-component-line="124" data-component-end-line="134" data-component-file="Header.tsx" data-component-name="Link" data-component-content="%7B%22elementName%22%3A%22Link%22%7D"
                key={link.label}
                href={link.href}
                className={`text-sm w-[73px] leading-line-16 text-center font-medium transition-colors duration-200 ${
                  isActive(link.href)
                    ? 'text-accent-main'
                    : 'text-text-primary hover:text-accent-main'
                }`}
              >
                {link.label}
              </Link>
            ))}
          </nav>

          {/* Login Button - Desktop */}
          <div data-component-id="src/components/common/Header.tsx:139:10" data-component-path="src/components/common/Header.tsx" data-component-line="139" data-component-end-line="149" data-component-file="Header.tsx" data-component-name="div" data-component-content="%7B%22elementName%22%3A%22div%22%2C%22className%22%3A%22hidden%20sm%3Ablock%22%7D" className="hidden sm:block">
            <Link data-component-id="src/components/common/Header.tsx:140:12" data-component-path="src/components/common/Header.tsx" data-component-line="140" data-component-end-line="148" data-component-file="Header.tsx" data-component-name="Link" data-component-content="%7B%22elementName%22%3A%22Link%22%2C%22href%22%3A%22https%3A%2F%2Fsiriolabs.ai%2Flogin%22%2C%22className%22%3A%22login-btn%20inline-flex%20items-center%20justify-center%20px-2.5%20py-1.5%20text-xs%20sm%3Apx-3%20sm%3Apy-2%20sm%3Atext-sm%20md%3Atext-sm%20font-medium%20bg-primary%20text-primary-fg%20hover%3Abg-action-2%20active%3Abg-primary-darker%20rounded-lg%20sm%3Arounded-lg%20md%3Arounded-lg%20transition-all%20duration-200%20ease-in-out%20focus%3Aoutline-none%20cursor-pointer%20touch-manipulation%22%7D"
              href="https://siriolabs.ai/login"
              target="_blank"
              rel="noopener noreferrer"
              className="login-btn inline-flex items-center justify-center px-2.5 py-1.5 text-xs sm:px-3 sm:py-2 sm:text-sm md:text-sm font-medium bg-primary text-primary-fg hover:bg-action-2 active:bg-primary-darker rounded-lg sm:rounded-lg md:rounded-lg transition-all duration-200 ease-in-out focus:outline-none cursor-pointer touch-manipulation"
              aria-label={ARIA_LABELS.buttons.login}
            >
              {CONTENT.navigation.loginText}
            </Link>
          </div>
        </div>

        {/* Mobile Navigation Overlay - Below Header */}
        {menuOpen && (
          <div data-component-id="src/components/common/Header.tsx:154:10" data-component-path="src/components/common/Header.tsx" data-component-line="154" data-component-end-line="191" data-component-file="Header.tsx" data-component-name="div" data-component-content="%7B%22elementName%22%3A%22div%22%2C%22className%22%3A%22fixed%20top-20%20left-0%20right-0%20bottom-0%20bg-action-2%20z-30%20sm%3Ahidden%22%7D" className="fixed top-20 left-0 right-0 bottom-0 bg-action-2 z-30 sm:hidden">
            <nav data-component-id="src/components/common/Header.tsx:155:12" data-component-path="src/components/common/Header.tsx" data-component-line="155" data-component-end-line="190" data-component-file="Header.tsx" data-component-name="nav" data-component-content="%7B%22elementName%22%3A%22nav%22%2C%22className%22%3A%22flex%20flex-col%20h-full%20px-4%20relative%22%7D" className="flex flex-col h-full px-4 relative" aria-label="Mobile navigation">
              {/* Navigation Links */}
              <div data-component-id="src/components/common/Header.tsx:157:14" data-component-path="src/components/common/Header.tsx" data-component-line="157" data-component-end-line="172" data-component-file="Header.tsx" data-component-name="div" data-component-content="%7B%22elementName%22%3A%22div%22%2C%22className%22%3A%22flex%20flex-col%20items-center%20gap-6%22%7D" className="flex flex-col items-center gap-6" style={{ paddingTop: '235px' }}>
                {CONTENT.navigation.links.map((link) => (
                  <Link data-component-id="src/components/common/Header.tsx:159:18" data-component-path="src/components/common/Header.tsx" data-component-line="159" data-component-end-line="170" data-component-file="Header.tsx" data-component-name="Link" data-component-content="%7B%22elementName%22%3A%22Link%22%7D"
                    key={link.label}
                    href={link.href}
                    className={`text-xl font-medium text-white-main text-center ${
                      isActive(link.href)
                        ? 'text-accent-main'
                        : 'text-text-primary hover:text-accent-main'
                    }`}
                    onClick={() => setMenuOpen(false)}
                  >
                    {link.label}
                  </Link>
                ))}
              </div>

              {/* Login Button */}
              <div data-component-id="src/components/common/Header.tsx:175:14" data-component-path="src/components/common/Header.tsx" data-component-line="175" data-component-end-line="189" data-component-file="Header.tsx" data-component-name="div" data-component-content="%7B%22elementName%22%3A%22div%22%2C%22className%22%3A%22absolute%20left-1%2F2%20transform%20-translate-x-1%2F2%22%7D"
                className="absolute left-1/2 transform -translate-x-1/2"
                style={{ bottom: '33px' }}
              >
                <Link data-component-id="src/components/common/Header.tsx:179:16" data-component-path="src/components/common/Header.tsx" data-component-line="179" data-component-end-line="188" data-component-file="Header.tsx" data-component-name="Link" data-component-content="%7B%22elementName%22%3A%22Link%22%2C%22href%22%3A%22https%3A%2F%2Fsiriolabs.ai%2Flogin%22%2C%22className%22%3A%22inline-flex%20items-center%20justify-center%20bg-main%20text-%5B16px%5D%20leading-%5B24px%5D%20h-12%20w-%5B163px%5D%20rounded-%5B12px%5D%20text-accent-main%20hover%3Abg-main%2F90%20transition-colors%20duration-200%20font-medium%22%2C%22textContent%22%3A%22Log%20in%22%7D"
                  href="https://siriolabs.ai/login"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="inline-flex items-center justify-center bg-main text-[16px] leading-[24px] h-12 w-[163px] rounded-[12px] text-accent-main hover:bg-main/90 transition-colors duration-200 font-medium"
                  onClick={() => setMenuOpen(false)}
                  aria-label="Log in to your account"
                >
                  Log in
                </Link>
              </div>
            </nav>
          </div>
        )}
      </div>
    </header>
  );
};

export default Header;
